import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CertificateProductFormFields,
} from "@/pages/platform/certificate-product-form-fields";
import {
  defaultChannelsForTargetType,
  emptyCertificateProductForm,
} from "@/pages/platform/certificate-product-form-defaults";
import type { CertificateProductFormValues } from "@/pages/platform/certificate-product-form-types";
import { parseCertificateProductFormToUpsert } from "@/pages/platform/certificate-product-form-submit";
import {
  getAllowedCertificateTargetTypes,
  getCertificateDocumentTypeOptions,
  upsertCertificateProductPrice,
} from "@/services/platformCertificatePricingService";

const QUERY_KEY = ["platform", "products"] as const;

export function CertificateProductNewPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CertificateProductFormValues>(emptyCertificateProductForm);

  const allowedTypesQuery = useQuery({
    queryKey: [...QUERY_KEY, "allowed-target-types"] as const,
    queryFn: getAllowedCertificateTargetTypes,
    staleTime: 300_000,
  });

  const docOptionsQuery = useQuery({
    queryKey: [...QUERY_KEY, "document-type-options"] as const,
    queryFn: getCertificateDocumentTypeOptions,
    staleTime: 300_000,
  });

  const saveMutation = useMutation({
    mutationFn: upsertCertificateProductPrice,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t("platform.products.saveSuccess"));
      navigate("/products");
    },
    onError: () => {
      toast.error(t("platform.products.saveError"));
    },
  });

  const handleAddTargetTypeChange = useCallback((value: string): void => {
    const ch = defaultChannelsForTargetType(value);
    setForm((f) => ({
      ...f,
      targetType: value,
      involvesAccountant: ch.involvesAccountant,
      involvesNotary: ch.involvesNotary,
    }));
  }, []);

  const toggleRequiredDoc = useCallback((slug: string, checked: boolean): void => {
    setForm((f) => ({
      ...f,
      requiredDocs: checked
        ? f.requiredDocs.includes(slug)
          ? f.requiredDocs
          : [...f.requiredDocs, slug]
        : f.requiredDocs.filter((s) => s !== slug),
    }));
  }, []);

  const toggleFulfillmentMode = useCallback((slug: string, checked: boolean): void => {
    setForm((f) => ({
      ...f,
      fulfillmentModes: checked
        ? f.fulfillmentModes.includes(slug)
          ? f.fulfillmentModes
          : [...f.fulfillmentModes, slug]
        : f.fulfillmentModes.filter((s) => s !== slug),
    }));
  }, []);

  const handleSubmit = useCallback((): void => {
    const parsed = parseCertificateProductFormToUpsert(form, "add", null);
    if (!parsed.ok) {
      toast.error(t(parsed.errorKey));
      return;
    }
    void saveMutation.mutate(parsed.body);
  }, [form, saveMutation, t]);

  const isSaving = saveMutation.isPending;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("platform.products.newPageTitle")}
        description={t("platform.products.newPageDescription")}
        onBack={() => navigate("/products")}
      />

      <div className="mt-4 space-y-4">
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <CertificateProductFormFields
              mode="add"
              suggestedTargetTypeSlugs={allowedTypesQuery.data ?? []}
              documentOptionsLoading={docOptionsQuery.isLoading}
              documentOptionSlugs={docOptionsQuery.data ?? []}
              isSaving={isSaving}
              form={form}
              setForm={setForm}
              onAddTargetTypeChange={handleAddTargetTypeChange}
              toggleRequiredDoc={toggleRequiredDoc}
              toggleFulfillmentMode={toggleFulfillmentMode}
            />
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="flex flex-row flex-wrap items-center justify-between gap-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/products")}
              disabled={isSaving}
              className="transition-colors duration-150"
            >
              {t("pages.common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="transition-colors duration-150"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  {t("platform.products.saving")}
                </>
              ) : (
                t("platform.products.save")
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
